import { useCallback, useRef, useState } from "react";
import { type AuthContextProps, useAuth } from "react-oidc-context";
import { PAGE_PATHS } from "@/config/urls";
import { useLogin } from "@/hooks/useLogin";
import { useOidcTokenRefresh } from "@/hooks/useOidcTokenRefresh";
import { saveOriginalUrl } from "@/lib/TokenManager";

const TOKEN_EXPIRATION_BUFFER_MS = 5000;

export const useAuthFlow = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const auth = useAuth();
  const { refreshAccessToken } = useOidcTokenRefresh();
  const { login } = useLogin();
  const latestAccessTokenRef = useRef<{
    token: string;
    expiresAt?: number;
  } | null>(null);
  const authPromiseRef = useRef<Promise<{
    success: boolean;
    error?: string;
  }> | null>(null);

  const isTokenExpired = useCallback((expiresAt?: number) => {
    if (!expiresAt) {
      return false;
    }

    // 5秒のバッファを持たせて有効期限をチェック
    return Date.now() >= expiresAt - TOKEN_EXPIRATION_BUFFER_MS;
  }, []);

  const tryHumhubLogin = useCallback(
    async (
      tokenInfo: { token: string; expiresAt?: number } | null | undefined,
    ) => {
      if (!tokenInfo?.token || isTokenExpired(tokenInfo.expiresAt)) {
        return false;
      }

      try {
        const result = await login(tokenInfo.token);
        if (result?.auth_token) {
          latestAccessTokenRef.current = {
            token: tokenInfo.token,
            expiresAt: tokenInfo.expiresAt,
          };
          return true;
        }
      } catch (_error) {
        // useLogin フック内でログ出力されるためここでは何もしない
      }

      return false;
    },
    [isTokenExpired, login],
  );

  /**
   * OIDC認証開始（コールバックURL検出対応）
   */
  const startOidcAuth = useCallback((auth: AuthContextProps) => {
    // 現在のURLが既にコールバックURLの場合は保存しない
    const currentUrl = window.location.href;
    if (!currentUrl.includes(PAGE_PATHS.CALLBACK)) {
      saveOriginalUrl(currentUrl);
    }

    // OIDC認証リダイレクト
    auth.signinRedirect();
  }, []);

  /**
   * 統合認証フロー実行
   */
  const executeAuthFlow = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (authPromiseRef.current) {
      return authPromiseRef.current;
    }

    let startedRedirect = false;
    const authPromise = (async () => {
      setIsAuthenticating(true);

      try {
        const tokenCandidates: Array<{ token: string; expiresAt?: number }> =
          [];

        if (
          auth.isAuthenticated &&
          auth.user?.access_token &&
          !auth.user.expired
        ) {
          const expiresAt =
            typeof auth.user.expires_at === "number"
              ? auth.user.expires_at * 1000
              : undefined;
          tokenCandidates.push({ token: auth.user.access_token, expiresAt });
        }

        if (
          latestAccessTokenRef.current &&
          !tokenCandidates.some(
            (candidate) =>
              candidate.token === latestAccessTokenRef.current?.token,
          )
        ) {
          tokenCandidates.push(latestAccessTokenRef.current);
        }

        for (const candidate of tokenCandidates) {
          if (await tryHumhubLogin(candidate)) {
            return { success: true };
          }
        }

        const refreshedToken = await refreshAccessToken();
        if (refreshedToken && (await tryHumhubLogin(refreshedToken))) {
          return { success: true };
        }

        // refreshトークンが無効でリダイレクトする場合、再度の多重リダイレクトを防ぐ
        startedRedirect = true;
        startOidcAuth(auth);
        return { success: false, error: "Redirecting to authentication" };
      } catch (error) {
        console.error("❌ Auth flow error:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      } finally {
        setIsAuthenticating(false);
        // リダイレクトを開始した場合は、新しい認証フロー開始を抑止するためにPromiseを維持する
        if (!startedRedirect) {
          authPromiseRef.current = null;
        }
      }
    })();

    authPromiseRef.current = authPromise;
    return authPromise;
  }, [auth, refreshAccessToken, startOidcAuth, tryHumhubLogin]);

  return {
    executeAuthFlow,
    isAuthenticating,
  };
};
